import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, useNavigationType } from "react-router-dom";
import { Search, Compass, Menu, User, LogOut, LibraryBig, CalendarClock, MapPin, Sparkles, BarChart3, ArrowLeft, ArrowRight, MessageSquare, ShoppingBag, KeyRound, Headphones } from "lucide-react";
import { CodexLockup } from "@/components/brand/CodexLockup";
import { TesseractIcon } from "@/components/brand/TesseractIcon";
import { ArchivistGlass } from "@/components/brand/ArchivistGlass";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { ProposeTopicDialog } from "@/components/ProposeTopicDialog";
import { QuickSearch } from "@/components/QuickSearch";
import { NotificationBell } from "@/components/NotificationBell";
import { MessagesBell } from "@/components/MessagesBell";
import { RoleBadge, isStaffRole } from "@/components/RoleBadge";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/explore", label: "Explore", icon: Compass, testid: "top-nav-categories-menu", desc: "Explore the Archive by category." },
  { to: "/timelines", label: "Timeline", icon: CalendarClock, testid: "top-nav-timeline-link", desc: "Trace history from 9600 BC to today." },
  { to: "/maps", label: "Atlas", icon: MapPin, testid: "top-nav-maps-link", desc: "Mysteries mapped around the world." },
  { to: "/universe", label: "Universe", icon: TesseractIcon, testid: "top-nav-universe-link", desc: "See how everything connects." },
  { to: "/archivist", label: "The Archivist", icon: ArchivistGlass, testid: "top-nav-archivist-link", desc: "Grounded in the Archive. Every answer cited." },
];

export function TopNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const [open, setOpen] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Browser-style history controls. React Router stores the session index on
  // window.history.state.idx; we track the furthest index reached so the
  // Forward button only enables when there is genuine forward history.
  const [histIdx, setHistIdx] = useState(() => (typeof window !== "undefined" ? window.history.state?.idx ?? 0 : 0));
  const [maxHistIdx, setMaxHistIdx] = useState(histIdx);
  useEffect(() => {
    const cur = window.history.state?.idx ?? 0;
    setHistIdx(cur);
    // A PUSH truncates any forward entries; POP/REPLACE keep the frontier.
    setMaxHistIdx((m) => (navType === "PUSH" ? cur : Math.max(m, cur)));
  }, [location, navType]);
  const canGoBack = histIdx > 0;
  const canGoForward = histIdx < maxHistIdx;

  // Global Cmd/Ctrl+K to open the quick topic finder.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");
  const premium = !!user?.subscription?.premium;

  return (
    <header
      className="sticky top-0 z-40 border-b border-white/5 codex-glass-strong"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingLeft: "env(safe-area-inset-left, 0px)",
        paddingRight: "env(safe-area-inset-right, 0px)",
      }}
    >
      <div className="mx-auto flex h-16 w-full max-w-[1200px] items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center" data-testid="top-nav-logo">
            <CodexLockup iconSize={44} showDivider showSubtitle={false} wordmarkSize="text-[26px]" />
          </Link>
          <div className="ml-4 hidden items-center gap-1 pl-1 sm:flex lg:ml-5" data-testid="nav-history-controls">
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={!canGoBack}
              data-testid="nav-back-button"
              aria-label="Go back"
              title="Back"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-muted-foreground transition-colors hover:border-gold-light/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-white/10 disabled:hover:text-muted-foreground"
            >
              <ArrowLeft className="h-4.5 w-4.5" />
            </button>
            <button
              type="button"
              onClick={() => navigate(1)}
              disabled={!canGoForward}
              data-testid="nav-forward-button"
              aria-label="Go forward"
              title="Forward"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-muted-foreground transition-colors hover:border-gold-light/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-white/10 disabled:hover:text-muted-foreground"
            >
              <ArrowRight className="h-4.5 w-4.5" />
            </button>
          </div>
        </div>

        <nav className="hidden items-center gap-1 lg:flex">
          <TooltipProvider delayDuration={250}>
            {LINKS.map((l) => {
              const Icon = l.icon;
              return (
                <Tooltip key={l.to}>
                  <TooltipTrigger asChild>
                    <Link
                      to={l.to}
                      data-testid={l.testid}
                      className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm transition-colors ${
                        isActive(l.to) ? "text-gold-light bg-white/[0.04]" : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Icon className="h-4 w-4" /> {l.label}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent sideOffset={6}>{l.desc}</TooltipContent>
                </Tooltip>
              );
            })}
            {(
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to="/store"
                    data-testid="top-nav-store-link"
                    className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm transition-colors ${
                      isActive("/store") ? "text-gold-light bg-white/[0.04]" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <ShoppingBag className="h-4 w-4" /> Store
                  </Link>
                </TooltipTrigger>
                <TooltipContent sideOffset={6}>Artifacts from the Archive.</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </nav>

        <div className="flex items-center gap-2">
          {!premium ? (
            <Link
              to="/subscribe"
              data-testid="nav-upgrade-button"
              title="Unlock Full Archive Access"
              className="hidden items-center gap-1.5 rounded-full border border-gold-light/40 px-3.5 py-1.5 text-xs font-semibold text-gold-light transition-colors hover:bg-gold-light/[0.1] sm:inline-flex"
            >
              <KeyRound className="h-3.5 w-3.5" /> Full Archive
            </Link>
          ) : null}
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="quick-search-trigger"
            aria-label="Search the archive"
            title="Search the archive (⌘K)"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-muted-foreground hover:border-gold-light/40 hover:text-foreground transition-colors"
          >
            <Search className="h-4.5 w-4.5" />
          </button>
          {user ? <MessagesBell /> : null}
          {user ? <NotificationBell /> : null}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="top-nav-profile-menu" className="inline-flex h-9 items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 text-sm hover:border-gold-light/40 transition-colors">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gold-light/20 text-gold-light text-xs font-semibold">
                    {user.name?.[0]?.toUpperCase() || "E"}
                  </span>
                  <span className="hidden max-w-[120px] truncate sm:inline">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">
                  <div className="flex items-center gap-2">
                    <span className="truncate">{user.email}</span>
                    <RoleBadge role={user.role} />
                    {premium ? (
                      <span data-testid="nav-member-badge" className="inline-flex items-center gap-1 rounded-full border border-gold-light/40 bg-gold-light/[0.08] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gold-light">
                        <KeyRound className="h-3 w-3" /> Member
                      </span>
                    ) : null}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <TooltipProvider delayDuration={250}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="nav-my-research">
                        <LibraryBig className="mr-2 h-4 w-4" /> My Research
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent side="left" sideOffset={8}>Your saved topics, notes &amp; collections</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem onClick={() => navigate("/subscribe")} data-testid="nav-membership">
                        <KeyRound className="mr-2 h-4 w-4" /> {premium ? "Membership" : "Full Archive Access"}
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent side="left" sideOffset={8}>
                      {premium ? "Manage your Full Archive membership" : "Unlock the complete archive"}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem onClick={() => navigate("/messages")} data-testid="nav-messages-menu">
                        <MessageSquare className="mr-2 h-4 w-4" /> Messages
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent side="left" sideOffset={8}>Your private conversations</TooltipContent>
                  </Tooltip>
                  {isStaffRole(user.role) ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuItem onClick={() => navigate("/metrics")} data-testid="nav-metrics">
                          <BarChart3 className="mr-2 h-4 w-4" /> Staff Metrics
                        </DropdownMenuItem>
                      </TooltipTrigger>
                      <TooltipContent side="left" sideOffset={8}>Site analytics &amp; activity</TooltipContent>
                    </Tooltip>
                  ) : null}
                  {isStaffRole(user.role) ? (
                    <DropdownMenuItem onClick={() => navigate("/admin/narration-pilot")} data-testid="nav-narration-pilot">
                      <Headphones className="mr-2 h-4 w-4" /> Narration Pilot
                    </DropdownMenuItem>
                  ) : null}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem onClick={() => setProposeOpen(true)} data-testid="nav-propose-topic">
                        <Sparkles className="mr-2 h-4 w-4" /> Suggest a Mystery
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent side="left" sideOffset={8}>Propose a case file for the archive</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout">
                        <LogOut className="mr-2 h-4 w-4" /> Sign out
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent side="left" sideOffset={8}>Log out of your account</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={() => navigate("/login")}
              data-testid="top-nav-signin-button"
              className="hidden lg:inline-flex h-9 items-center gap-1.5 rounded-full px-4 text-sm font-semibold text-[color:var(--codex-gold-ink)]"
              style={{ backgroundColor: "#D8B66A" }}
            >
              <User className="h-4 w-4" /> Sign in
            </button>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 lg:hidden" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="codex-glass-strong w-72 border-white/10" style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top, 0px))" }}>
              <SheetHeader>
                <SheetTitle className="font-wordmark tracking-[0.24em] text-gold-light">CODEX</SheetTitle>
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground/70">Archive of the Unexplained</div>
              </SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                {LINKS.map((l) => {
                  const Icon = l.icon;
                  return (
                    <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-white/5">
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gold-light" />
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground">{l.label}</span>
                        <span className="block text-[11px] leading-snug text-muted-foreground/70">{l.desc}</span>
                      </span>
                    </Link>
                  );
                })}
                {(
                  <Link to="/store" onClick={() => setOpen(false)} data-testid="mobile-store-link" className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-white/5">
                    <ShoppingBag className="mt-0.5 h-4 w-4 shrink-0 text-gold-light" />
                    <span className="min-w-0"><span className="block text-sm text-foreground">Store</span><span className="block text-[11px] leading-snug text-muted-foreground/70">Artifacts from the Archive.</span></span>
                  </Link>
                )}
                <div className="my-2 h-px bg-white/10" />
                <Link to="/subscribe" onClick={() => setOpen(false)} data-testid="mobile-membership-link" className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-white/5">
                  <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-gold-light" />
                  <span className="min-w-0"><span className="block text-sm text-foreground">{premium ? "Membership" : "Full Archive Access"}</span><span className="block text-[11px] leading-snug text-muted-foreground/70">{premium ? "Manage your membership" : "Unlock the complete archive"}</span></span>
                </Link>
                {user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setOpen(false)} className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-white/5"><LibraryBig className="mt-0.5 h-4 w-4 shrink-0 text-gold-light" /><span className="min-w-0"><span className="block text-sm">My Research</span><span className="block text-[11px] leading-snug text-muted-foreground/70">Your saved topics, notes &amp; collections</span></span></Link>
                    <Link to="/messages" onClick={() => setOpen(false)} data-testid="mobile-messages-link" className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-white/5"><MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-gold-light" /><span className="min-w-0"><span className="block text-sm">Messages</span><span className="block text-[11px] leading-snug text-muted-foreground/70">Your private conversations</span></span></Link>
                    {isStaffRole(user.role) ? (
                      <Link to="/metrics" onClick={() => setOpen(false)} data-testid="mobile-metrics-link" className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-white/5"><BarChart3 className="mt-0.5 h-4 w-4 shrink-0 text-gold-light" /><span className="min-w-0"><span className="block text-sm">Staff Metrics</span><span className="block text-[11px] leading-snug text-muted-foreground/70">Site analytics &amp; activity</span></span></Link>
                    ) : null}
                    {isStaffRole(user.role) ? (
                      <Link to="/admin/narration-pilot" onClick={() => setOpen(false)} data-testid="mobile-narration-pilot-link" className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-white/5"><Headphones className="mt-0.5 h-4 w-4 shrink-0 text-gold-light" /><span className="min-w-0"><span className="block text-sm">Narration Pilot</span><span className="block text-[11px] leading-snug text-muted-foreground/70">A/B voice quality preview</span></span></Link>
                    ) : null}
                    <button onClick={() => { setOpen(false); setProposeOpen(true); }} className="flex items-start gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-white/5" data-testid="mobile-propose-topic"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-gold-light" /><span className="min-w-0"><span className="block text-sm">Suggest a Mystery</span><span className="block text-[11px] leading-snug text-muted-foreground/70">Propose a case file for the archive</span></span></button>
                    <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="flex items-start gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-white/5"><LogOut className="mt-0.5 h-4 w-4 shrink-0 text-gold-light" /><span className="min-w-0"><span className="block text-sm">Sign out</span><span className="block text-[11px] leading-snug text-muted-foreground/70">Log out of your account</span></span></button>
                  </>
                ) : (
                  <Link to="/login" onClick={() => setOpen(false)} className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-white/5"><User className="mt-0.5 h-4 w-4 shrink-0 text-gold-light" /><span className="min-w-0"><span className="block text-sm">Sign in</span><span className="block text-[11px] leading-snug text-muted-foreground/70">Access your research account</span></span></Link>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      {user ? <ProposeTopicDialog open={proposeOpen} onOpenChange={setProposeOpen} /> : null}
      <QuickSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
}
